<template>
    <div class="-mt-5">

        <sau-loading v-if="! ready">
            <template slot="message">Fetching Account Holder Details</template>
        </sau-loading>

        <template v-else-if="$root.requiresAdditionalUserInformation">
            <h3 class="text-xl mb-2">Billing Details</h3>

            <template v-if="! contactHasPermissions">
                <p>Before an order can be placed we need billing details for this account.</p>
                <p>Unfortunately, you have not been granted permission to set this.</p>
                <p>Please contact {{ accountHolder.firstname }} {{ accountHolder.lastname}} (account holder) for access.</p>
            </template>
            <template v-else>
                <form>
                    <div class="row mb-2">
                        <div class="form-group col">
                            <label for="address1" class="text-sm">Address 1</label>
                            <input class="form-control" id="address1" type="text" required v-model="address1">
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="form-group col">
                            <label for="address2" class="text-sm">Address 2 <span class="text-muted ml-2 text-xs">(Optional)</span></label>
                            <input class="form-control" id="address2" type="text" v-model="address2">
                        </div>
                    </div>
                    <div class="flex flex-col lg:flex-row lg:-m-2">
                        <div class="w-full lg:w-2/3 lg:m-2 mb-2">
                            <label for="city" class="text-sm">City</label>
                            <input class="form-control" id="city" type="text" required v-model="city">
                        </div>
                        <div class="w-full lg:w-1/3 lg:m-2 mb-2">
                            <label for="postcode" class="text-sm">Postcode</label>
                            <input class="form-control" id="postcode" type="text" required v-model="postcode">
                        </div>
                    </div>
                    <div class="flex flex-col lg:flex-row my-2">
                        <div class="form-group">
                            <label for="country" class="text-sm">Country</label>
                            <country-select
                                id="country"
                                class="form-control"
                                v-model="country"
                                :country="country"
                                topCountry="AU"
                            />
                        </div>
                    </div>
                    <div class="flex flex-col lg:flex-row mb-2">
                        <div class="form-group w-100">
                            <label for="state" class="text-sm">State/Region</label>
                            <region-select
                                id="state"
                                class="form-control"
                                v-model="state"
                                :country="country"
                                :region="state"
                                :regionName="true"
                                :placeholder="'Select State/Region'"
                                :disabled="! country"
                            />
                        </div>
                    </div>
                    <div class="row mt-8">
                        <div class="form-group col mb-3">
                            <label for="phonenumber" class="text-sm">Phone Number</label>
                            <input class="form-control" id="phonenumber" type="text" required v-model="phonenumber">
                        </div>
                    </div>
                    <div class="row mt-8">
                        <div class="col mb-3">

                            <template v-if="requiredFieldsHaveBeenFilled"><!-- :-/ because bootstrap-vue tooltips are the worst -->
                                <button
                                    id="account-details-tooltip"
                                    class="btn btn-success d-block w-100"
                                    :class="{ 'btn-light' :  updating }"
                                    @click.prevent="updateBillingDetails()"
                                    :disabled="! requiredFieldsHaveBeenFilled || updating"
                                >
                                Next <i v-if="updating" class="far fa-spinner-third fa-spin"></i></button>
                            </template>
                            <template v-else>
                                <span v-b-tooltip.hover title="Please fill out the fields above to continue">
                                    <button class="btn btn-light d-block w-100" disabled>Next</button>
                                </span>
                            </template>

                        </div>
                    </div>
                </form>
            </template>
        </template>
    </div>
</template>

<script>
import { RegionSelect, CountrySelect } from 'vue-country-region-select';

export default {

    components: {CountrySelect, RegionSelect},

    inject: [
        'refreshOrder',
    ],

    data() {
        return {
            address1: '',
            address2: '',
            city: '',
            postcode: '',
            state: '',
            country: '',
            phonenumber: '',
            ready: false,
            updating: false,
            requiredFields: ['address1', 'city', 'postcode', 'state', 'country', 'phonenumber'],
        }
    },

    mounted() {
        this.vuexLoading().then(() => {

            if(! Object.keys(this.accountHolder).length) { // Preloaded for ordering

                this.getAccountHolder().then(response => {
                    this.$store.commit('setAccountHolder', response.data);
                    [...this.requiredFields, 'address2'].map(field => this[field] = this.accountHolder[field]);

                }).finally(() => {
                    this.ready = true;
                });

            } else {

                [...this.requiredFields, 'address2'].map(field => this[field] = this.accountHolder[field]);
                this.ready = true;
            }
        })
    },

    methods: {
        updateBillingDetails() {
            this.updating = true;

            let data = [...this.requiredFields, 'address2']
                .map(field => ({ key: field, value: this[field] }))
                .reduce((obj, item) => {
                    obj[item.key] = item.value;
                    return obj;
                }, {});

            if(this.userDetails.whmcs_contact_id) data.subaccount = true;

            this.putRequest(`/api/clients/${this.userDetails.whmcs_client_id}`, data)
                .then(response => {

                    this.$swal({
                        toast: true,
                        type:'success',
                        html: 'Billing details updated successfully. <br /> Please note that the prices may have been adjusted after applying any tax relevant to your country.',
                        showConfirmButton: false,
                        timer: 2000,
                    });

                    // Get account holder details to recalculate tax for display purposes
                    this.getAccountHolder().then(response => {
                        this.$store.commit('setAccountHolder', response.data);
                    }).then(response => {
                        this.refreshOrder();
                    });

                    // Add note about prices being updated too - and call summary to recalculate the prices
                    this.updateBillingDetailsInVuex();

                }).catch(error => {

                    console.log('error', error);

                    this.$swal({
                        toast: true,
                        type:'error',
                        title: 'Failed to update billing details',
                        showConfirmButton: false,
                        timer: 2000,
                    });

                }).finally(() => {
                    this.updating = false;
                })
        },
        updateBillingDetailsInVuex() {
            this.requiredFields.map(field => this.accountHolder[field] = this[field]);
        }
    },
    computed: {
        requiredFieldsHaveBeenFilled() {
            if(!this.ready) return;

            return this.requiredFields.every(field => this[field] != '');
        },
        contactHasPermissions() {
            if(!this.ready) return;
            // User is not a contact
            if(this.userDetails.whmcs_contact_id === 0) return true;
            // Does contact have permission to update master account?
            return this.userDetails.permissions.includes('profile') ? true : false;
        }
    }
};
</script>
